import {
  getContentOwnerBindingRow,
  getDb,
  BIBLE_PROGRESS_STORE,
  type BibleProgressLastRead,
  type BibleProgressRecentReads,
  type BibleRecentRead,
} from './db';
import {
  getAuthEpoch,
  getAuthUserId,
  waitForAuthResolution,
} from './keepsake/epochs';

import { getReadingAccessSnapshot } from './reading-access';

const LAST_READ_KEY = 'last-read' as const;
const RECENT_READS_KEY = 'recent-reads' as const;
const LOCAL_READ_ONLY_OWNER = 'local-read-only';
export const RECENT_READS_CAP = 20;

export type { BibleRecentRead };

type BibleProgressRowKind = typeof LAST_READ_KEY | typeof RECENT_READS_KEY;

interface BibleProgressContext {
  ownerId: string;
  authEpoch: number;
  probeGeneration: number | null;
}

let probeOwnerOverride: string | null | undefined;
let probeOwnerGeneration = 0;
let progressProbe: ((phase: 'after-read') => Promise<void>) | null = null;
let writeCommitProbe: (() => void) | null = null;

/** Stable owner-compounded key; old global `last-read` rows are never adopted. */
export function bibleProgressScopedKey(
  ownerId: string,
  kind: BibleProgressRowKind,
): string {
  if (ownerId.trim() === '' || ownerId.includes(':')) {
    throw new Error('bible-progress-owner-invalid');
  }
  return `${ownerId}:${kind}`;
}

/** Probe-only deterministic owner source; undefined restores real auth. */
export function __setBibleProgressOwnerForProbe(
  ownerId: string | null | undefined,
): void {
  probeOwnerOverride = ownerId;
  probeOwnerGeneration += 1;
}

/** Probe-only pause after a read and before its final owner check. */
export function __setBibleProgressReadProbe(
  probe: ((phase: 'after-read') => Promise<void>) | null,
): void {
  progressProbe = probe;
}

/** Probe-only synchronous auth transition after writes are queued, pre-commit. */
export function __setBibleProgressWriteCommitProbe(
  probe: (() => void) | null,
): void {
  writeCommitProbe = probe;
}

async function resolveProgressContext(): Promise<BibleProgressContext | null> {
  if (probeOwnerOverride !== undefined) {
    return {
      ownerId: probeOwnerOverride ?? LOCAL_READ_ONLY_OWNER,
      authEpoch: 0,
      probeGeneration: probeOwnerGeneration,
    };
  }

  await waitForAuthResolution();
  const authEpoch = getAuthEpoch();
  const authOwner = getAuthUserId();
  if (authOwner === undefined) return null;
  if (authOwner === null) {
    return {
      ownerId: LOCAL_READ_ONLY_OWNER,
      authEpoch,
      probeGeneration: null,
    };
  }

  // OwnerGate establishes this durable binding before Bible routes mount.
  // Rechecking it here keeps direct callers fail-closed as well.
  const boundOwner = await getContentOwnerBindingRow();
  if (getAuthEpoch() !== authEpoch || getAuthUserId() !== authOwner) return null;
  if (boundOwner !== authOwner) return null;
  return { ownerId: authOwner, authEpoch, probeGeneration: null };
}

function contextIsCurrent(context: BibleProgressContext): boolean {
  if (getReadingAccessSnapshot().restricted) return false;
  if (context.probeGeneration !== null) {
    return (
      probeOwnerOverride !== undefined &&
      probeOwnerGeneration === context.probeGeneration &&
      (probeOwnerOverride ?? LOCAL_READ_ONLY_OWNER) === context.ownerId
    );
  }
  const currentOwner = getAuthUserId();
  return (
    getAuthEpoch() === context.authEpoch &&
    (currentOwner ?? LOCAL_READ_ONLY_OWNER) === context.ownerId
  );
}

function rowBelongsToOwner(
  row: BibleProgressLastRead | BibleProgressRecentReads | undefined,
  context: BibleProgressContext,
  kind: BibleProgressRowKind,
): boolean {
  return Boolean(
    row &&
      row.ownerId === context.ownerId &&
      row.key === bibleProgressScopedKey(context.ownerId, kind),
  );
}

export async function getLastRead(): Promise<BibleProgressLastRead | null> {
  const access = getReadingAccessSnapshot();
  if (access.restricted) return access.local?.lastRead ?? null;
  const context = await resolveProgressContext();
  if (!context) return null;
  const db = await getDb();
  const key = bibleProgressScopedKey(context.ownerId, LAST_READ_KEY);
  const result = (await db.get(BIBLE_PROGRESS_STORE, key)) as
    | BibleProgressLastRead
    | undefined;
  await progressProbe?.('after-read');
  if (!contextIsCurrent(context)) return null;
  return rowBelongsToOwner(result, context, LAST_READ_KEY) ? (result ?? null) : null;
}

export async function getRecentReads(): Promise<BibleRecentRead[]> {
  const access = getReadingAccessSnapshot();
  if (access.restricted) return [...(access.local?.recentReads ?? [])];
  const context = await resolveProgressContext();
  if (!context) return [];
  const db = await getDb();
  const key = bibleProgressScopedKey(context.ownerId, RECENT_READS_KEY);
  const row = (await db.get(BIBLE_PROGRESS_STORE, key)) as
    | BibleProgressRecentReads
    | undefined;
  await progressProbe?.('after-read');
  if (!contextIsCurrent(context)) return [];
  return rowBelongsToOwner(row, context, RECENT_READS_KEY) && Array.isArray(row?.entries)
    ? row.entries
    : [];
}

export async function getRecentRead(
  bookSlug: string,
  chapter: number,
): Promise<BibleRecentRead | null> {
  const entries = await getRecentReads();
  return entries.find((e) => e.bookSlug === bookSlug && e.chapter === chapter) ?? null;
}

export interface SetLastReadOptions {
  /** Nearest visible verse at checkpoint time. Invalid values are dropped. */
  verse?: number;
  /** A lookup updates history but does not replace the deep-read resume slot. */
  lookup?: boolean;
}

export async function setLastRead(
  bookSlug: string,
  chapter: number,
  scrollOffset: number,
  options?: SetLastReadOptions,
): Promise<void> {
  if (getReadingAccessSnapshot().restricted) return;
  if (!bookSlug || !Number.isInteger(chapter) || chapter < 1) return;
  const context = await resolveProgressContext();
  if (!context || !contextIsCurrent(context)) return;
  const safeOffset = Number.isFinite(scrollOffset) && scrollOffset >= 0
    ? Math.floor(scrollOffset)
    : 0;
  const verse =
    options?.verse !== undefined &&
    Number.isInteger(options.verse) &&
    options.verse >= 1
      ? options.verse
      : undefined;
  const db = await getDb();
  if (!contextIsCurrent(context)) return;
  const lastReadKey = bibleProgressScopedKey(context.ownerId, LAST_READ_KEY);
  const recentReadsKey = bibleProgressScopedKey(context.ownerId, RECENT_READS_KEY);
  const timestamp = Date.now();
  const tx = db.transaction(BIBLE_PROGRESS_STORE, 'readwrite');
  const store = tx.objectStore(BIBLE_PROGRESS_STORE);
  try {
    const existing = (await store.get(recentReadsKey)) as
      | BibleProgressRecentReads
      | undefined;
    if (!contextIsCurrent(context)) {
      tx.abort();
      await tx.done.catch(() => {});
      return;
    }
    const prev = rowBelongsToOwner(existing, context, RECENT_READS_KEY) &&
      Array.isArray(existing?.entries)
      ? existing.entries
      : [];
    const entry: BibleRecentRead = {
      bookSlug,
      chapter,
      scrollOffset: safeOffset,
      timestamp,
      ...(verse !== undefined ? { verse } : {}),
    };
    const next = [
      entry,
      ...prev.filter((e) => !(e.bookSlug === bookSlug && e.chapter === chapter)),
    ].slice(0, RECENT_READS_CAP);
    const writes: Array<Promise<unknown>> = [];
    if (!options?.lookup) {
      writes.push(store.put({
        key: lastReadKey,
        ownerId: context.ownerId,
        bookSlug,
        chapter,
        scrollOffset: safeOffset,
        timestamp,
      } satisfies BibleProgressLastRead));
    }
    writes.push(store.put({
      key: recentReadsKey,
      ownerId: context.ownerId,
      entries: next,
    } satisfies BibleProgressRecentReads));
    await Promise.all(writes);
    writeCommitProbe?.();
    if (!contextIsCurrent(context)) {
      tx.abort();
      await tx.done.catch(() => {});
      return;
    }
    await tx.done;
  } catch (error) {
    try {
      tx.abort();
    } catch {
      // Transaction may already be complete or auto-aborted.
    }
    await tx.done.catch(() => {});
    throw error;
  }
}

export async function clearLastRead(): Promise<void> {
  if (getReadingAccessSnapshot().restricted) return;
  const context = await resolveProgressContext();
  if (!context || !contextIsCurrent(context)) return;
  const db = await getDb();
  if (!contextIsCurrent(context)) return;
  const tx = db.transaction(BIBLE_PROGRESS_STORE, 'readwrite');
  try {
    await tx.store.delete(bibleProgressScopedKey(context.ownerId, LAST_READ_KEY));
    writeCommitProbe?.();
    if (!contextIsCurrent(context)) {
      tx.abort();
      await tx.done.catch(() => {});
      return;
    }
    await tx.done;
  } catch (error) {
    try {
      tx.abort();
    } catch {
      // Transaction may already be complete or auto-aborted.
    }
    await tx.done.catch(() => {});
    throw error;
  }
}
